// Navbar.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import logoImage from './Static_resources/aishang_guangbozhan_logo.png';
import logoImage2 from './Static_resources/HiRadio数字广播系统logo002.png';
import { API_BASE_URL } from '../api';

const MOBILE_BREAKPOINT = 768;

const Navbar = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isProfileHovered, setIsProfileHovered] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    
    const checkAuthAndUnread = async () => {
      const loggedIn = isTokenValid();
      setIsLoggedIn(loggedIn);
      
      if (loggedIn) {
        await checkUnreadMessages();
      }
    };
    
    checkAuthAndUnread();
    
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isTokenValid = () => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    try {
      const decoded = jwtDecode(token);
      return decoded.exp > Date.now() / 1000;
    } catch {
      return false;
    }
  };

  // 独立检查未读消息
  const checkUnreadMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_BASE_URL}/api/media/messages/unread-counts`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const { comments, likes, system } = response.data.data;
        // 只要有一个分类有未读消息就显示红点
        setHasUnreadMessages(comments > 0 || likes > 0 || system > 0);
      }
    } catch (error) {
      console.error('检查未读消息失败:', error);
    }
  };

  // 修正红点样式 - 确保一定是圆形
  const badgeStyle = {
    position: 'absolute',
    top: '1px',
    right: '1px',
    width: '10px',
    height: '10px',
    backgroundColor: '#ff4d4f',
    borderRadius: '50%',
    border: '2px solid #fff',
    boxSizing: 'border-box'
  };
  const badgeWithCountStyle = {
    position: 'absolute',
    top: '8px',
    right: '10px',
    width: '10px',
    height: '10px',
    backgroundColor: '#ff4d4f',
    borderRadius: '50%',
    border: '2px solid #fff',
    boxSizing: 'border-box'
  };

  return (
    <nav style={styles.navbar}>
      <div style={styles.navContainer}>
        {/* 左侧logo */}
        <div style={styles.leftSection}>
          <a href="https://guangbozhan.cn" style={styles.brandLeft} aria-label="返回首页">
            <img
              src={logoImage}
              alt="山村学校广播站"
              style={styles.logoPrimary}
              height={44}
              loading="eager"
            />
          </a>
        </div>

        {/* 中间logo - 现在可以点击了 */}
        {!isMobile && (
          <div style={styles.centerSection}>
            <Link to="/" style={styles.brandCenter} aria-label="广播节目">
              <img
                src={logoImage2}
                alt="HiRadio 广播播出系统"
                style={styles.logoSecondary}
                height={22}
                loading="lazy"
              />
            </Link>
          </div>
        )}

        {/* 右侧个人中心/注册 */}
        <div style={styles.rightSection}>
          {isLoggedIn ? (
            <div
              style={styles.profileContainer}
              onMouseEnter={() => setIsProfileHovered(true)}
              onMouseLeave={() => setIsProfileHovered(false)}
            >
              <button type="button" style={styles.profileButton}>
                个人中心
                {hasUnreadMessages && (
                  <span style={badgeStyle}></span>
                )}
              </button>
              {isProfileHovered && (
                <div style={styles.profileDropdown}>
                  <ul style={styles.profileDropdownList}>
                    <li>
                      <Link to="/profile-upload" style={styles.menuLink}>
                        个人资料
                      </Link>
                    </li>
                    <li>
                      <Link to="/my-audios" style={styles.menuLink}>
                        我的音频
                      </Link>
                    </li>
                    <li>
                      <Link to="/my-audio-collections" style={styles.menuLink}>
                        我的合集
                      </Link>
                    </li>
                    <li style={{ position: 'relative' }}>
                      <Link to="/messages/comments" style={styles.menuLink}>
                        消息中心
                        {hasUnreadMessages && (
                          <span style={badgeWithCountStyle}></span>
                        )}
                      </Link>
                    </li>
                    <li>
                      <Link to="/mailbox-list" style={styles.menuLink}>
                        HiRadio 官方信箱
                      </Link>
                    </li>
                    <li>
                      <Link to="/manual" style={styles.menuLink}>
                        HiRadio 使用手册
                      </Link>
                    </li>
                    <li>
                      <Link to="/logout" style={styles.menuLink}>
                        登出
                      </Link>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/register"
              style={{
                ...styles.cta,
                ...(location.pathname === '/register' ? styles.ctaActive : {}),
              }}
            >
              注册 / 登录
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
};

// 样式保持不变
const styles = {
  navbar: {
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    backgroundColor: '#fff',
    borderBottom: '1px solid #E9ECEF',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    padding: '0 16px',
  },
  navContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '64px',
    maxWidth: '100%',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flex: '0 0 auto',
    paddingRight: '20px',
    marginLeft: '40px',
  },
  centerSection: {
    display: 'flex',
    alignItems: 'center',
    flex: '1 1 auto',
    justifyContent: 'center',
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    flex: '0 0 auto',
  },
  brandLeft: {
    display: 'inline-flex',
    alignItems: 'center',
    textDecoration: 'none',
    userSelect: 'none',
  },
  logoPrimary: {
    display: 'block',
    height: 55,
    width: 'auto',
  },
  brandCenter: {
    display: 'inline-flex',
    alignItems: 'center',
    textDecoration: 'none',
    userSelect: 'none',
    cursor: 'pointer',
    transition: 'transform 0.2s ease',
    borderRadius: 4,
  },
  logoSecondary: {
    display: 'block',
    height: 44,
    width: 'auto',
    borderRadius: 4,
  },
  cta: {
    padding: '8px 14px',
    borderRadius: 8,
    background: '#0052d9',
    color: '#fff',
    textDecoration: 'none',
    fontSize: 14,
    lineHeight: 1,
  },
  ctaActive: { 
    background: '#0a60ff' 
  },
  profileContainer: { 
    position: 'relative' 
  },
  profileButton: {
    position: 'relative',
    padding: '8px 14px',
    borderRadius: 8,
    border: '1px solid #E5E7EB',
    background: '#F8FAFC',
    fontSize: 14,
    cursor: 'pointer',
  },
  profileDropdown: {
    position: 'absolute',
    top: '75%',
    right: 0,
    background: '#fff',
    border: '1px solid #E5E7EB',
    boxShadow: '0 10px 20px rgba(0,0,0,.08)',
    borderRadius: 10,
    width: 200,
    overflow: 'hidden',
    zIndex: 1001,
    marginTop: '8px',
  },
  profileDropdownList: {
    listStyle: 'none',
    margin: 0,
    padding: '6px 0',
  },
  menuLink: {
    position: 'relative',
    display: 'block',
    padding: '10px 14px',
    color: '#374151',
    textDecoration: 'none',
    fontSize: 14,
    '&:hover': {
      backgroundColor: '#F3F4F6',
    }
  },
};

export default Navbar;